import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DescriptionIcon from "@mui/icons-material/Description";
import UploadIcon from "@mui/icons-material/Upload";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteIcon from "@mui/icons-material/Delete";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import type { ImportItem } from "../../../../api/types";
import { DateTimeDisplay } from "../../../../components/DateTimeDisplay";
import { getImportWarnings } from "../warnings";

export type ImportActionsProps = {
  item: ImportItem;
  /** Omit to hide the Edit action (e.g. when already inside the editor modal). */
  onEdit?: (item: ImportItem) => void;
  onViewTransactions: (item: ImportItem) => void;
  onViewCsv: (item: ImportItem) => void;
  onOpenDiff: (item: ImportItem) => void;
  onRerun: (item: ImportItem) => void;
  isRerunning?: boolean;
  onDelete?: (item: ImportItem) => void;
  onRestore?: (item: ImportItem) => void;
  /** Re-open a period that was finalized from its invoice. */
  onClearFinalized?: (item: ImportItem) => void;
  size?: "small" | "medium";
};

export const ImportActions = ({
  item,
  onEdit,
  onViewTransactions,
  onViewCsv,
  onOpenDiff,
  onRerun,
  isRerunning = false,
  onDelete,
  onRestore,
  onClearFinalized,
  size = "medium",
}: ImportActionsProps) => {
  const pendingCount = item.pendingDiffCount ?? 0;
  const isDeleted = !!item.deletedAt;
  const spinnerSize = size === "small" ? 16 : 20;
  // Pending-diff warnings already show as the count badge on the upload icon;
  // the rest ("needs re-run", missing/partial date range) are what a re-run
  // resolves, so we surface them on the re-run icon to make the fix obvious.
  const rerunWarnings = getImportWarnings(item).filter(
    (w) => w.code !== "pending_diffs",
  );
  const rerunTooltip =
    rerunWarnings.length > 0 ? (
      <Box component="span">
        Re-run to resolve:
        {rerunWarnings.map((w) => (
          <Box component="span" key={w.code} sx={{ display: "block" }}>
            • {w.message}
          </Box>
        ))}
      </Box>
    ) : (
      "Re-run import (re-process current file)"
    );
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      {onEdit && (
        <Tooltip title="Edit import">
          <IconButton
            size={size}
            onClick={() => onEdit(item)}
            aria-label="Edit import"
          >
            <EditIcon fontSize={size} />
          </IconButton>
        </Tooltip>
      )}
      {!isDeleted && (
        <Tooltip title="View transactions">
          <IconButton
            size={size}
            onClick={() => onViewTransactions(item)}
            aria-label="View transactions"
          >
            <ReceiptLongIcon fontSize={size} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="View CSV">
        <IconButton
          size={size}
          onClick={() => onViewCsv(item)}
          aria-label="View CSV"
        >
          <DescriptionIcon fontSize={size} />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={
          pendingCount > 0 ? (
            <>
              {pendingCount} pending update{pendingCount === 1 ? "" : "s"} —
              click to review
              {item.latestPendingDiffAt && (
                <>
                  <br />
                  Latest from{" "}
                  <DateTimeDisplay date={item.latestPendingDiffAt} />
                </>
              )}
            </>
          ) : (
            "Upload new version"
          )
        }
      >
        <IconButton
          size={size}
          onClick={() => onOpenDiff(item)}
          aria-label={
            pendingCount > 0 ? "Review pending updates" : "Upload new version"
          }
        >
          <Badge
            badgeContent={pendingCount || undefined}
            color="warning"
            overlap="circular"
          >
            <UploadIcon fontSize={size} />
          </Badge>
        </IconButton>
      </Tooltip>
      {!isDeleted && (
        <Tooltip title={rerunTooltip}>
          <span>
            <IconButton
              size={size}
              onClick={() => onRerun(item)}
              disabled={isRerunning}
              aria-label="Re-run import"
            >
              <Badge
                color="warning"
                variant="dot"
                overlap="circular"
                invisible={rerunWarnings.length === 0}
              >
                {isRerunning ? (
                  <CircularProgress size={spinnerSize} />
                ) : (
                  <ReplayIcon fontSize={size} />
                )}
              </Badge>
            </IconButton>
          </span>
        </Tooltip>
      )}
      {item.finalizedAt && onClearFinalized && (
        <Tooltip title="Re-open this period to statement uploads. The invoice's transactions stay; only the lock is cleared.">
          <IconButton
            size={size}
            onClick={() => onClearFinalized(item)}
            aria-label="Clear finalized"
          >
            <LockOpenIcon fontSize={size} />
          </IconButton>
        </Tooltip>
      )}
      {isDeleted
        ? onRestore && (
            <Tooltip title="Restore import">
              <IconButton
                size={size}
                onClick={() => onRestore(item)}
                aria-label="Restore import"
              >
                <RestoreFromTrashIcon fontSize={size} />
              </IconButton>
            </Tooltip>
          )
        : onDelete && (
            <Tooltip title="Delete import">
              <IconButton
                size={size}
                color="error"
                onClick={() => onDelete(item)}
                aria-label="Delete import"
              >
                <DeleteIcon fontSize={size} />
              </IconButton>
            </Tooltip>
          )}
    </Stack>
  );
};
